import React, { useEffect, useMemo, useRef, useState } from 'react'

type Role = 'Founder' | 'CTO / Engineering' | 'Product' | 'Data / ML' | 'Other'

type FormData = {
  role: Role | ''
  roleOther?: string
  company: string
  size: 'Pre-product' | '1-10' | '11-50' | '51-200' | '200+' | ''
  needs: Array<'Product feature' | 'Integration / API' | 'Data / ML pipeline' | 'Agentic workflow' | 'Cloud infra / CI/CD'>
  timeline: 'ASAP' | '2–4 weeks' | '1–3 months' | 'Exploring' | ''
  budget: '<$10k' | '$10–30k' | '$30–100k' | '$100k+' | ''
  name: string
  email: string
  summary?: string
}

const initial: FormData = {
  role: '',
  company: '',
  size: '',
  needs: [],
  timeline: '',
  budget: '',
  name: '',
  email: '',
}

function Progress({ step, total }: { step: number; total: number }) {
  const pct = Math.round(((step + 1) / total) * 100)
  return (
    <div className="progress">
      <div className="progress__bar" style={{ width: `${pct}%` }} />
      <div className="progress__label">{pct}%</div>
    </div>
  )
}

function FieldError({ children }: { children?: React.ReactNode }) {
  if (!children) return null
  return <div className="field-error">{children}</div>
}

export default function Questionnaire({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<FormData>(initial)
  const [step, setStep] = useState(0)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // One question per screen; dynamic sequence
  const sequence = useMemo(() => {
    const seq: Array<keyof FormData | 'roleOther' | 'summary'> = ['role']
    if (data.role === 'Other') seq.push('roleOther')
    seq.push('company', 'size', 'needs', 'timeline', 'budget', 'name', 'email', 'summary')
    return seq
  }, [data.role])

  const total = sequence.length
  useEffect(() => {
    if (step > total - 1) setStep(total - 1)
  }, [total, step])

  function validateCurrent() {
    const key = sequence[step]
    const e: Record<string, string> = {}
    if (key === 'role' && !data.role) e.role = 'Select your role'
    if (key === 'roleOther' && !data.roleOther) e.roleOther = 'Tell us your role'
    if (key === 'company' && !data.company) e.company = 'Company or team name is required'
    if (key === 'size' && !data.size) e.size = 'Select a size range'
    if (key === 'needs' && !data.needs.length) e.needs = 'Pick at least one area'
    if (key === 'timeline' && !data.timeline) e.timeline = 'Select a timeline'
    if (key === 'budget' && !data.budget) e.budget = 'Select a budget range'
    if (key === 'name' && !data.name) e.name = 'Your name is required'
    if (key === 'email' && (!data.email || !/^\S+@\S+\.\S+$/.test(data.email))) e.email = 'Enter a valid email'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function next() {
    if (!validateCurrent()) return
    setStep((s) => Math.min(s + 1, total - 1))
  }
  function back() {
    setStep((s) => Math.max(s - 1, 0))
  }

  const summary = useMemo(() => {
    const needs = data.needs.join(', ')
    return `New project enquiry\n\n` +
      `Role: ${data.role}${data.role === 'Other' && data.roleOther ? ` (${data.roleOther})` : ''}\n` +
      `Company: ${data.company}\n` +
      `Size: ${data.size}\n` +
      `Needs: ${needs}\n` +
      `Timeline: ${data.timeline}\n` +
      `Budget: ${data.budget}\n` +
      `Name: ${data.name}\n` +
      `Email: ${data.email}\n` +
      (data.summary ? `\nNotes:\n${data.summary}\n` : '')
  }, [data])

  async function submit() {
    // Validate all required fields and jump to the first missing
    const order: Array<keyof FormData | 'roleOther'> = ['role']
    if (data.role === 'Other') order.push('roleOther')
    order.push('company','size','needs','timeline','budget','name','email')
    const e: Record<string, string> = {}
    for (const key of order) {
      if (key === 'role' && !data.role) { e.role = 'Select your role'; setErrors(e); setStep(order.indexOf('role')); return }
      if (key === 'roleOther' && !data.roleOther) { e.roleOther = 'Tell us your role'; setErrors(e); setStep(order.indexOf('roleOther')); return }
      if (key === 'company' && !data.company) { e.company = 'Company or team name is required'; setErrors(e); setStep(order.indexOf('company')); return }
      if (key === 'size' && !data.size) { e.size = 'Select a size range'; setErrors(e); setStep(order.indexOf('size')); return }
      if (key === 'needs' && !data.needs.length) { e.needs = 'Pick at least one area'; setErrors(e); setStep(order.indexOf('needs')); return }
      if (key === 'timeline' && !data.timeline) { e.timeline = 'Select a timeline'; setErrors(e); setStep(order.indexOf('timeline')); return }
      if (key === 'budget' && !data.budget) { e.budget = 'Select a budget range'; setErrors(e); setStep(order.indexOf('budget')); return }
      if (key === 'name' && !data.name) { e.name = 'Your name is required'; setErrors(e); setStep(order.indexOf('name')); return }
      if (key === 'email' && (!data.email || !/^\S+@\S+\.\S+$/.test(data.email))) { e.email = 'Enter a valid email'; setErrors(e); setStep(order.indexOf('email')); return }
    }
    try {
      const resp = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!resp.ok) throw new Error('Submission failed')
      setStep(0)
      setData(initial)
      alert('Thanks! Your project enquiry has been received.')
    } catch (e) {
      alert('Sorry, something went wrong. Please email hello@intellectus.fun')
    }
  }

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  useEffect(() => { inputRef.current?.focus() }, [step])

  const StepRole = (
    <div>
      <h2>Your role</h2>
      <p className="muted">Helps tailor how we collaborate.</p>
      <div className="choices">
        {(['Founder','CTO / Engineering','Product','Data / ML','Other'] as Role[]).map((r) => (
          <label key={r} className={`choice ${data.role === r ? 'selected' : ''}`}>
            <input type="radio" name="role" value={r}
              checked={data.role === r}
              onChange={() => {
                setData({ ...data, role: r })
                if (r !== 'Other') setTimeout(next, 0)
              }} />
            <span>{r}</span>
          </label>
        ))}
      </div>
      <FieldError>{errors.role}</FieldError>
    </div>
  )

  const StepRoleOther = (
    <div>
      <h2>Your role</h2>
      <p className="muted">Tell us more.</p>
      <div className="row">
        <input
          ref={inputRef as any}
          placeholder="Your role"
          value={data.roleOther || ''}
          onChange={(e) => setData({ ...data, roleOther: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') next() }}
        />
        <FieldError>{errors.roleOther}</FieldError>
      </div>
    </div>
  )

  const StepCompany = (
    <div>
      <h2>Your company</h2>
      <p className="muted">Basic context for the engagement.</p>
      <div className="row">
        <input
          ref={inputRef as any}
          placeholder="Company or team name"
          autoComplete="organization"
          value={data.company}
          onChange={(e) => setData({ ...data, company: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') next() }}
        />
        <FieldError>{errors.company}</FieldError>
      </div>
    </div>
  )

  const needsList: FormData['needs'] = ['Product feature','Integration / API','Data / ML pipeline','Agentic workflow','Cloud infra / CI/CD']
  const StepNeeds = (
    <div>
      <h2>What do you want to build?</h2>
      <p className="muted">Pick one or more.</p>
      <div className="choices grid">
        {needsList.map((n) => {
          const on = data.needs.includes(n)
          return (
            <label key={n} className={`choice ${on ? 'selected' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => {
                setData({
                  ...data,
                  needs: on ? data.needs.filter((x) => x !== n) : [...data.needs, n],
                })
              }} />
              <span>{n}</span>
            </label>
          )
        })}
      </div>
      <FieldError>{errors.needs}</FieldError>
    </div>
  )

  const StepSize = (
    <div>
      <h2>Company size</h2>
      <div className="row">
        <select value={data.size} onChange={(e) => { setData({ ...data, size: e.target.value as any }); setTimeout(next, 0) }}>
          <option value="">Company size</option>
          <option>Pre-product</option>
          <option>1-10</option>
          <option>11-50</option>
          <option>51-200</option>
          <option>200+</option>
        </select>
        <FieldError>{errors.size}</FieldError>
      </div>
    </div>
  )

  const StepTimeline = (
    <div>
      <h2>Timeline</h2>
      <div className="row">
        <select value={data.timeline} onChange={(e) => { setData({ ...data, timeline: e.target.value as any }); setTimeout(next, 0) }}>
          <option value="">Timeline</option>
          <option>ASAP</option>
          <option>2–4 weeks</option>
          <option>1–3 months</option>
          <option>Exploring</option>
        </select>
        <FieldError>{errors.timeline}</FieldError>
      </div>
    </div>
  )

  const StepBudget = (
    <div>
      <h2>Budget (rough)</h2>
      <div className="row">
        <select value={data.budget} onChange={(e) => { setData({ ...data, budget: e.target.value as any }); setTimeout(next, 0) }}>
          <option value="">Budget (rough)</option>
          <option>&lt;$10k</option>
          <option>$10–30k</option>
          <option>$30–100k</option>
          <option>$100k+</option>
        </select>
        <FieldError>{errors.budget}</FieldError>
      </div>
    </div>
  )

  const StepName = (
    <div>
      <h2>Your name</h2>
      <div className="row">
        <input ref={inputRef as any} placeholder="Your name" autoComplete="name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') next() }} />
        <FieldError>{errors.name}</FieldError>
      </div>
    </div>
  )

  const StepEmail = (
    <div>
      <h2>Work email</h2>
      <div className="row">
        <input ref={inputRef as any} type="email" placeholder="you@company.com" autoComplete="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') next() }} />
        <FieldError>{errors.email}</FieldError>
      </div>
    </div>
  )

  const StepSummary = (
    <div>
      <h2>Notes (optional)</h2>
      <div className="row">
        <textarea ref={inputRef as any} placeholder="Brief summary or links (optional)" rows={4} value={data.summary || ''} onChange={(e) => setData({ ...data, summary: e.target.value })} />
      </div>
      <div className="row">
        <pre className="summary" aria-label="Summary preview">{summary}</pre>
      </div>
    </div>
  )

  const currentKey = sequence[step]
  const content = (
    currentKey === 'role' ? StepRole :
    currentKey === 'roleOther' ? StepRoleOther :
    currentKey === 'company' ? StepCompany :
    currentKey === 'size' ? StepSize :
    currentKey === 'needs' ? StepNeeds :
    currentKey === 'timeline' ? StepTimeline :
    currentKey === 'budget' ? StepBudget :
    currentKey === 'name' ? StepName :
    currentKey === 'email' ? StepEmail :
    StepSummary
  )

  return (
    <div className="modal">
      <div className="modal__card">
        <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        <Progress step={step} total={total} />
        {content}
        <div className="modal__actions">
          {step > 0 ? (
            <button onClick={back} className="btn ghost">Back</button>
          ) : <span />}
          {step < total - 1 ? (
            <button onClick={next} className="btn primary">Next</button>
          ) : (
            <button onClick={submit} className="btn primary">Send</button>
          )}
        </div>
      </div>
    </div>
  )
}
